import { useState, useEffect, useCallback, useRef } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Trash2, Eye, Heart, Play, ExternalLink, EyeOff, Check, Loader2, X, AlertCircle } from "lucide-react";

const formatCount = (n) => {
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return String(n || 0);
};

const AdminShorts = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCreator, setFilterCreator] = useState("");
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]); // [{id, file, status: 'pending'|'uploading'|'done'|'error', error?, shortId?}]
  const fileInputRef = useRef(null);

  // Upload form state
  const [creatorUsername, setCreatorUsername] = useState("");
  const [caption, setCaption] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [ctaLabel, setCtaLabel] = useState("Ver perfil");
  const [hashtags, setHashtags] = useState("");
  const [niche, setNiche] = useState("");

  const NICHES = [
    { key: "latinas", label: "Latinas" },
    { key: "fitness", label: "Fitness" },
    { key: "playboy", label: "Playboy" },
    { key: "alternativas", label: "Alternativas" },
    { key: "cosplay", label: "Cosplay" },
    { key: "premium", label: "Premium VIP" },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filterCreator ? `?creator=${filterCreator}` : "";
      const [shortsR, creatorsR] = await Promise.all([
        fetch(`/api/admin/shorts${q}`, { headers: { ...getAuthHeaders() } }),
        fetch(`/api/admin/creators`, { headers: { ...getAuthHeaders() } }),
      ]);
      const sd = await shortsR.json();
      const cd = await creatorsR.json();
      setItems(sd.items || []);
      const creatorList = Array.isArray(cd) ? cd : cd.creators || [];
      setCreators(creatorList.filter((c) => c.active !== false));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filterCreator]);

  useEffect(() => { load(); }, [load]);

  // ===== Batch upload =====
  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!creatorUsername) {
      toast.error("Selecciona una modelo primero");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const oversized = files.filter((f) => f.size > 60 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} archivo(s) superan 60MB. Comprime antes de subir.`);
    }
    const accepted = files.filter((f) => f.size <= 60 * 1024 * 1024);
    setQueue(accepted.map((file, i) => ({
      id: `${Date.now()}-${i}`,
      file,
      status: "pending",
    })));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFromQueue = (id) => {
    setQueue((q) => q.filter((it) => it.id !== id));
  };

  const uploadOne = async (item) => {
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("creatorUsername", creatorUsername);
    fd.append("caption", caption);
    fd.append("ctaUrl", ctaUrl);
    fd.append("ctaLabel", ctaLabel);
    fd.append("hashtags", hashtags);
    fd.append("niche", niche);
    fd.append("active", "true");
    const r = await fetch(`/api/admin/shorts/upload`, {
      method: "POST",
      headers: { ...getAuthHeaders() },
      body: fd,
    });
    let d = null;
    let rawText = "";
    try { d = await r.clone().json(); }
    catch { try { rawText = await r.text(); } catch { /* ignore */ } }
    if (!r.ok) {
      const msg = (d && (d.detail || d.message)) || rawText.slice(0, 180) || `HTTP ${r.status} ${r.statusText}`;
      console.error("[shorts upload] failed", { file: item.file.name, status: r.status, body: d || rawText });
      throw new Error(msg);
    }
    return d;
  };

  const runQueue = async () => {
    if (queue.length === 0 || uploading) return;
    if (!creatorUsername) {
      toast.error("Selecciona una modelo primero");
      return;
    }
    setUploading(true);
    let done = 0;
    let errors = 0;
    for (const item of queue) {
      // mark uploading
      setQueue((q) => q.map((it) => it.id === item.id ? { ...it, status: "uploading" } : it));
      try {
        const result = await uploadOne(item);
        setQueue((q) => q.map((it) => it.id === item.id ? { ...it, status: "done", shortId: result?.id } : it));
        done += 1;
      } catch (err) {
        setQueue((q) => q.map((it) => it.id === item.id ? { ...it, status: "error", error: err?.message || "Falló" } : it));
        errors += 1;
      }
    }
    setUploading(false);
    if (errors === 0) {
      toast.success(`${done} short(s) publicado(s) para @${creatorUsername}`);
      setCaption("");
      setCtaUrl("");
      // Keep hashtags + niche so next batch is faster
      // Clear queue after a short delay so user sees the green checkmarks
      setTimeout(() => setQueue([]), 1200);
    } else {
      toast.error(`${done} subido(s), ${errors} con error. Revisa la lista.`);
    }
    load();
  };

  const clearQueue = () => {
    if (uploading) return;
    setQueue([]);
  };

  const toggleActive = async (s) => {
    const r = await fetch(`/api/admin/shorts/${s.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({ active: !s.active }),
    });
    if (r.ok) {
      toast.success(s.active ? "Oculto" : "Publicado");
      load();
    } else {
      toast.error("Error");
    }
  };

  const deleteShort = async (id) => {
    if (!window.confirm("¿Borrar este short? No se puede deshacer.")) return;
    const r = await fetch(`/api/admin/shorts/${id}`, {
      method: "DELETE",
      headers: { ...getAuthHeaders() },
    });
    if (r.ok) {
      toast.success("Borrado");
      setItems((it) => it.filter((x) => x.id !== id));
    } else {
      toast.error("Error");
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="admin-shorts-page">
        <h1 className="text-2xl md:text-3xl font-bold mb-1">Shorts</h1>
        <p className="text-white/60 text-sm mb-6">
          Sube clips verticales (60s máx, 60MB máx). Decides a qué modelo asignarlos. Solo se muestran en el feed las modelos que tú elijas.
        </p>

        {/* Upload form */}
        <div className="bg-gradient-to-br from-pink-500/5 to-purple-500/5 border border-pink-500/20 rounded-2xl p-5 mb-8">
          <h2 className="font-bold text-sm mb-3 flex items-center gap-2">
            <Upload className="w-4 h-4 text-pink-300" />
            Subir nuevo short
          </h2>
          {creatorUsername && (() => {
            const c = creators.find((x) => x.username === creatorUsername);
            if (c && !c.shortsEnabled) {
              return (
                <div className="mb-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
                  ⚠️ @{creatorUsername} no tiene Shorts activos. Sube igual, pero no aparecerá en el feed público hasta que actives &quot;Shorts&quot; en{" "}
                  <a href="/admin/creadoras" className="underline">Creadoras</a>.
                </div>
              );
            }
            return null;
          })()}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Modelo *</label>
              <select
                value={creatorUsername}
                onChange={(e) => setCreatorUsername(e.target.value)}
                data-testid="shorts-upload-creator-select"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">-- Elegir modelo --</option>
                {creators.map((c) => (
                  <option key={c.username} value={c.username}>
                    {c.name || c.username} (@{c.username})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">CTA label</label>
              <input
                type="text"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                placeholder="Ver perfil / Suscribirme"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Caption</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Texto que aparece sobre el video"
                rows={2}
                maxLength={500}
                data-testid="shorts-upload-caption"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">CTA URL (link &quot;Ver más&quot;)</label>
              <input
                type="text"
                value={ctaUrl}
                onChange={(e) => setCtaUrl(e.target.value)}
                placeholder={`https://jglinks.co/${creatorUsername || "username"} o /jgfans/${creatorUsername || "username"}`}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-[10px] text-white/40 mt-1">Vacío = sin botón. Tip: usa `/{`{username}`}` o `/jgfans/{`{username}`}` para máxima conversión.</p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Hashtags</label>
              <input
                type="text"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="latina colombia fitness"
                data-testid="shorts-upload-hashtags"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
              <p className="text-[10px] text-white/40 mt-1">
                Separa con espacios. Máx 12, sin signos especiales. El &quot;#&quot; es opcional.
              </p>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-white/50 mb-1 block">Nicho</label>
              <select
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                data-testid="shorts-upload-niche"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">-- Sin nicho --</option>
                {NICHES.map((n) => (
                  <option key={n.key} value={n.key}>{n.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-white/40 mt-1">
                Aparece en la pestaña Nichos del feed público.
              </p>
            </div>
            <div className="md:col-span-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv,.m4v"
                multiple
                onChange={onFilesPicked}
                disabled={uploading || !creatorUsername}
                data-testid="shorts-upload-file"
                className="block w-full text-sm text-white/80 file:mr-3 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:bg-pink-500 file:text-white file:font-semibold file:cursor-pointer file:hover:bg-pink-600 disabled:opacity-50"
              />
              <p className="text-[10px] text-white/40 mt-1">
                Puedes seleccionar varios videos (Ctrl/Cmd + click). Todos se publicarán para <span className="text-pink-300 font-medium">@{creatorUsername || "—"}</span> con la misma caption, hashtags y nicho.
              </p>
            </div>
          </div>

          {/* Batch queue */}
          {queue.length > 0 && (
            <div className="mt-5 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-bold">
                    Lote: {queue.length} video{queue.length === 1 ? "" : "s"}
                    {uploading && (
                      <span className="ml-2 text-pink-300 text-xs">
                        ({queue.filter(it => it.status === "done").length} subidos / {queue.filter(it => it.status === "error").length} con error)
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-white/40">
                    Se procesan uno a uno. No cierres esta pestaña hasta terminar.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={clearQueue}
                    disabled={uploading}
                    data-testid="shorts-clear-queue"
                    className="px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40"
                  >
                    Limpiar
                  </button>
                  <button
                    onClick={runQueue}
                    disabled={uploading || queue.every(it => it.status === "done" || it.status === "error")}
                    data-testid="shorts-run-queue"
                    className="px-4 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {uploading ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" />Subiendo lote…</>
                    ) : (
                      <><Upload className="w-3.5 h-3.5" />Subir lote</>
                    )}
                  </button>
                </div>
              </div>
              <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {queue.map((it) => {
                  const sizeMb = (it.file.size / (1024 * 1024)).toFixed(1);
                  return (
                    <li
                      key={it.id}
                      data-testid={`queue-item-${it.id}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs border ${
                        it.status === "done"
                          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                          : it.status === "error"
                            ? "bg-red-500/10 border-red-500/30 text-red-200"
                            : it.status === "uploading"
                              ? "bg-pink-500/10 border-pink-500/40 text-pink-100 animate-pulse"
                              : "bg-white/[0.03] border-white/10 text-white/80"
                      }`}
                    >
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                        {it.status === "done" && <Check className="w-4 h-4" />}
                        {it.status === "error" && <AlertCircle className="w-4 h-4" />}
                        {it.status === "uploading" && <Loader2 className="w-4 h-4 animate-spin" />}
                        {it.status === "pending" && <Play className="w-3.5 h-3.5 opacity-50" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{it.file.name}</p>
                        <p className="text-[10px] opacity-60">
                          {sizeMb}MB
                          {it.status === "error" && it.error && <> · <span className="text-red-300">{it.error}</span></>}
                          {it.status === "done" && it.shortId && <> · ID {it.shortId.slice(0, 8)}…</>}
                        </p>
                      </div>
                      {!uploading && it.status === "pending" && (
                        <button
                          onClick={() => removeFromQueue(it.id)}
                          className="opacity-60 hover:opacity-100"
                          title="Quitar del lote"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-4">
          <label className="text-xs text-white/60">Filtrar:</label>
          <select
            value={filterCreator}
            onChange={(e) => setFilterCreator(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">Todas</option>
            {creators.map((c) => <option key={c.username} value={c.username}>@{c.username}</option>)}
          </select>
          <span className="text-xs text-white/40">{items.length} resultado(s)</span>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-white/50 text-sm">Cargando…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-white/50 text-sm border border-dashed border-white/10 rounded-xl">
            No hay shorts {filterCreator && `para @${filterCreator}`} todavía
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((s) => (
              <div
                key={s.id}
                data-testid={`admin-short-card-${s.id}`}
                className={`relative bg-zinc-900 border rounded-xl overflow-hidden ${s.active ? "border-white/10" : "border-red-500/30 opacity-60"}`}
              >
                <div className="aspect-[9/16] bg-black relative">
                  {s.thumbnailUrl ? (
                    <img src={s.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/30">▶</div>
                  )}
                  {!s.active && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-red-500/80 text-white text-[10px] font-bold uppercase">
                      Oculto
                    </div>
                  )}
                </div>
                <div className="p-2.5 space-y-1.5">
                  <p className="text-xs font-bold truncate">@{s.creatorUsername}</p>
                  {s.caption && <p className="text-[11px] text-white/60 line-clamp-2">{s.caption}</p>}
                  {(s.niche || (s.hashtags && s.hashtags.length > 0)) && (
                    <div className="flex flex-wrap gap-1 items-center pt-0.5">
                      {s.niche && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-200 border border-amber-400/20">
                          {s.niche}
                        </span>
                      )}
                      {(s.hashtags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] text-rose-200/80">#{t}</span>
                      ))}
                      {(s.hashtags || []).length > 3 && (
                        <span className="text-[10px] text-white/40">+{s.hashtags.length - 3}</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-[10px] text-white/50">
                    <span className="flex items-center gap-0.5"><Play className="w-3 h-3" />{formatCount(s.views)}</span>
                    <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{formatCount(s.likes)}</span>
                  </div>
                  <div className="flex items-center gap-1 pt-1">
                    <a
                      href={`/shorts/${s.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir"
                      className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-white/70"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button
                      onClick={() => toggleActive(s)}
                      title={s.active ? "Ocultar" : "Publicar"}
                      data-testid={`admin-short-toggle-${s.id}`}
                      className="p-1.5 rounded bg-white/5 hover:bg-white/10 text-white/70"
                    >
                      {s.active ? <EyeOff className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5 text-emerald-300" />}
                    </button>
                    <button
                      onClick={() => deleteShort(s.id)}
                      title="Borrar"
                      data-testid={`admin-short-delete-${s.id}`}
                      className="p-1.5 rounded bg-white/5 hover:bg-red-500/20 text-red-300 ml-auto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// silence unused
const _silent = Eye;
export { _silent };

export default AdminShorts;
